import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import IconButton from '@mui/material/IconButton'
import Snackbar from '@mui/material/Snackbar'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tabs from '@mui/material/Tabs'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import type { TabuExtract } from '@lawyer/shared'
import { useState } from 'react'
import { formatDateTime } from '../../lib/format'

type Props = {
  open: boolean
  documentName: string
  extract: TabuExtract
  onClose: () => void
}

/** גוש/חלקה first — it is what gets copied into contracts all day. */
export function TabuExtractDialog({ open, documentName, extract, onClose }: Props) {
  const [tab, setTab] = useState<'data' | 'raw'>('data')
  const [copied, setCopied] = useState(false)

  const copy = (text: string) => {
    void navigator.clipboard?.writeText(text)
    setCopied(true)
  }

  const gushLine = [
    extract.gush && `גוש ${extract.gush}`,
    extract.helka && `חלקה ${extract.helka}`,
    extract.subHelka && `תת חלקה ${extract.subHelka}`,
  ]
    .filter(Boolean)
    .join(' ')

  const copyAll = () => {
    const owners = extract.owners
      .map((o) => `${o.name}${o.idNumber ? ` (${o.idType ?? 'ת"ז'} ${o.idNumber})` : ''}${o.share ? ` — ${o.share}` : ''}`)
      .join(' · ')
    copy(
      [
        `נסח טאבו${extract.registryOffice ? ` — לשכת רישום ${extract.registryOffice}` : ''}${extract.producedDate ? `, הופק ${extract.producedDate}` : ''}`,
        gushLine,
        extract.areaSqm && `שטח: ${extract.areaSqm} מ"ר`,
        extract.propertyShare && `החלק בנכס: ${extract.propertyShare}`,
        owners && `בעלויות: ${owners}`,
        extract.mortgages.length ? `משכנתאות: ${extract.mortgages.join(' · ')}` : null,
        extract.notes.length ? `הערות: ${extract.notes.join(' · ')}` : null,
      ]
        .filter(Boolean)
        .join('\n')
    )
  }

  const row = (label: string, value: string | null, copyText?: string) => (
    <TableRow key={label}>
      <TableCell sx={{ color: 'text.secondary', width: 150 }}>{label}</TableCell>
      <TableCell sx={{ fontWeight: 500 }}>
        {value ?? (
          <Typography variant="body2" component="span" color="warning.main">
            לא זוהה בנסח
          </Typography>
        )}
      </TableCell>
      <TableCell align="left" sx={{ width: 48 }}>
        {value ? (
          <Tooltip title="העתקה">
            <IconButton size="small" onClick={() => copy(copyText ?? value)}>
              <ContentCopyIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        ) : null}
      </TableCell>
    </TableRow>
  )

  const listSection = (label: string, items: string[]) =>
    row(label, items.length ? items.join(' · ') : 'אין')

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="baseline" flexWrap="wrap" gap={1}>
          <span>נתוני נסח טאבו</span>
          <Chip
            size="small"
            variant="outlined"
            color="secondary"
            label={`חולץ ${formatDateTime(extract.extractedAt)}`}
          />
        </Stack>
        <Typography variant="caption" color="text.secondary" component="div">
          מתוך: {documentName}
          {extract.registryOffice ? ` · לשכת רישום ${extract.registryOffice}` : ''}
          {extract.producedDate ? ` · הופק ${extract.producedDate}` : ''}
        </Typography>
        <Tabs value={tab} onChange={(_, v: 'data' | 'raw') => setTab(v)} sx={{ minHeight: 36, mt: 1 }}>
          <Tab value="data" label="נתונים" sx={{ minHeight: 36 }} />
          <Tab value="raw" label="טקסט גולמי" sx={{ minHeight: 36 }} />
        </Tabs>
      </DialogTitle>

      <DialogContent dividers>
        {tab === 'data' ? (
          <>
            {gushLine ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  bgcolor: (t) => `${t.palette.secondary.main}14`,
                  border: 1,
                  borderColor: 'secondary.light',
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  mb: 2,
                }}
              >
                <div>
                  <Typography variant="caption" color="secondary.dark" fontWeight={600}>
                    גוש / חלקה{extract.subHelka ? ' / תת-חלקה' : ''}
                  </Typography>
                  <Typography variant="h6" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                    {[extract.gush, extract.helka, extract.subHelka].filter(Boolean).join(' / ')}
                  </Typography>
                </div>
                <Button variant="contained" size="small" onClick={() => copy(gushLine)}>
                  העתקה
                </Button>
              </Box>
            ) : (
              <Alert severity="warning" sx={{ mb: 2 }}>
                לא זוהו נתוני גוש וחלקה — ראו את הטקסט הגולמי.
              </Alert>
            )}

            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              פרטי הנכס
            </Typography>
            <Table size="small">
              <TableBody>
                {row('גוש', extract.gush)}
                {row('חלקה', extract.helka)}
                {extract.subHelka ? row('תת-חלקה', extract.subHelka) : null}
                {row('שטח רשום', extract.areaSqm ? `${extract.areaSqm} מ"ר` : null, extract.areaSqm ?? undefined)}
                {row('סוג מקרקעין', extract.landType)}
                {row('רשות', extract.authority)}
                {extract.address ? row('כתובת', extract.address) : null}
                {extract.floor ? row('קומה', extract.floor) : null}
                {extract.attachments ? row('הצמדות', extract.attachments) : null}
                {row('החלק בנכס', extract.propertyShare)}
                {extract.oldParcelNumbers ? row('מספרים ישנים', extract.oldParcelNumbers) : null}
              </TableBody>
            </Table>

            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }} gutterBottom>
              בעלויות
            </Typography>
            {extract.owners.length ? (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>שם</TableCell>
                    <TableCell>זיהוי</TableCell>
                    <TableCell>חלק</TableCell>
                    <TableCell>מהות</TableCell>
                    <TableCell>שטר</TableCell>
                    <TableCell align="left" sx={{ width: 48 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {extract.owners.map((o, i) => (
                    <TableRow key={i}>
                      <TableCell sx={{ fontWeight: 500 }}>{o.name}</TableCell>
                      <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {o.idNumber ? `${o.idType ?? ''} ${o.idNumber}`.trim() : '—'}
                      </TableCell>
                      <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{o.share ?? '—'}</TableCell>
                      <TableCell>{[o.action, o.date].filter(Boolean).join(' · ') || '—'}</TableCell>
                      <TableCell sx={{ fontVariantNumeric: 'tabular-nums' }}>{o.deedNumber ?? '—'}</TableCell>
                      <TableCell align="left">
                        <Tooltip title="העתקת שורת הבעלים">
                          <IconButton
                            size="small"
                            onClick={() =>
                              copy(
                                `${o.name}${o.idNumber ? `, ${o.idType ?? 'ת"ז'} ${o.idNumber}` : ''}${o.share ? `, ${o.share}` : ''}`
                              )
                            }
                          >
                            <ContentCopyIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <Typography variant="body2" color="warning.main">
                לא זוהו בעלויות — ראו את הטקסט הגולמי.
              </Typography>
            )}

            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }} gutterBottom>
              שעבודים והערות
            </Typography>
            <Table size="small">
              <TableBody>
                {listSection('משכנתאות', extract.mortgages)}
                {listSection('הערות', extract.notes)}
                {listSection('עיקולים', extract.seizures)}
                {listSection('חכירות', extract.leases)}
              </TableBody>
            </Table>
          </>
        ) : (
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 1.5,
              bgcolor: 'background.default',
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              fontSize: 13,
              whiteSpace: 'pre-wrap',
              fontFamily: 'inherit',
            }}
          >
            {extract.rawText}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between' }}>
        <Typography variant="caption" color="text.secondary">
          הנתונים חולצו מהקובץ המקורי · ליד כל שדה כפתור העתקה
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button onClick={onClose}>סגירה</Button>
          <Button variant="contained" onClick={copyAll}>
            העתקת הכל
          </Button>
        </Stack>
      </DialogActions>

      <Snackbar
        open={copied}
        autoHideDuration={1500}
        onClose={() => setCopied(false)}
        message="הועתק ללוח"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Dialog>
  )
}
